
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import chatWallpaper from "./chatbackground.jpg";

import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  Button,
  Typography,
  Badge,
  InputAdornment,
  Drawer,
  Tooltip,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Fade,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import CircleIcon from "@mui/icons-material/Circle";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";

const COLORS = {
  primary: "#075E54",
  primaryLight: "#128C7E",
  accent: "#25D366",
  ink: "#172B32",
  muted: "#75858C",
  border: "#E5EBEF",
  surface: "#FFFFFF",
  canvas: "#F3F6F8",
  incoming: "#FFFFFF",
  outgoing: "#DDF8CC",
  danger: "#D64545",
};

const formatTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatLastSeen = (user) => {
  if (user?.is_online) return "Active now";

  return user?.last_seen_display || "Offline";
};

const getSenderId = (sender) => {
  if (sender && typeof sender === "object") {
    return Number(sender.id ?? sender.pk ?? sender.user_id);
  }

  return Number(sender);
};

const getDisplayName = (user) => {
  return (
    user?.username ||
    user?.full_name ||
    user?.name ||
    "School member"
  );
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return "U";

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const normalizeMessage = (data) => ({
  ...data,
  id: data.id ?? data.pk ?? `${data.timestamp ?? ""}-${Math.random()}`,
  sender: data.sender_id ?? data.sender,
  receiver: data.receiver_id ?? data.receiver,
  message: data.message ?? "",
  image: data.image || null,
  timestamp: data.timestamp || new Date().toISOString(),
  sender_profile_image: data.sender_profile_image || null,
});

const mergeMessages = (previous, incoming) => {
  const result = [...previous];
  const knownIds = new Set(
    previous.map((item) => String(item.id))
  );

  incoming.forEach((item) => {
    const normalized = normalizeMessage(item);
    const id = String(normalized.id);

    if (!knownIds.has(id)) {
      knownIds.add(id);
      result.push(normalized);
    }
  });

  return result.sort(
    (a, b) =>
      new Date(a.timestamp).getTime() -
      new Date(b.timestamp).getTime()
  );
};

const ChatAvatar = ({
  user,
  size = 48,
  showOnline = true,
}) => {
  const name = getDisplayName(user);

  return (
    <Badge
      overlap="circular"
      variant="dot"
      invisible={!showOnline || !user?.is_online}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      sx={{
        "& .MuiBadge-badge": {
          width: 12,
          height: 12,
          minWidth: 12,
          borderRadius: "50%",
          backgroundColor: COLORS.accent,
          border: "2px solid #FFFFFF",
          boxShadow: "0 0 0 1px rgba(37,211,102,.10)",
        },
      }}
    >
      <Avatar
        src={user?.profile_image || undefined}
        alt={name}
        imgProps={{ loading: "lazy" }}
        sx={{
          width: size,
          height: size,
          fontSize: size * 0.34,
          fontWeight: 800,
          color: COLORS.primary,
          bgcolor: "#E0F2EC",
          border: "1px solid rgba(7,94,84,.08)",
          boxShadow: "0 3px 10px rgba(20,45,55,.07)",
        }}
      >
        {getInitials(name)}
      </Avatar>
    </Badge>
  );
};

const ChatApp = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem("access");

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [usersLoading, setUsersLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [connectionState, setConnectionState] = useState("disconnected");

  const [errorMessage, setErrorMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const socketRef = useRef(null);
  const selectedUserRef = useRef(null);
  const typingTimeout = useRef(null);
  const reconnectTimeout = useRef(null);
  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const imageInputRef = useRef(null);
  const mountedRef = useRef(false);
  const sendingRef = useRef(false);
  const historyRequestRef = useRef(0);
  const socketGenerationRef = useRef(0);
  const shouldReconnectRef = useRef(false);

  const savedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const identity = useMemo(() => {
    try {
      if (!token) {
        return {
          id: savedUser.id ?? null,
          role: String(
            savedUser.user_type || savedUser.role || ""
          )
            .toLowerCase()
            .trim()
            .replace(/_/g, " "),
        };
      }

      const decoded = jwtDecode(token);

      return {
        id:
          decoded.user_id ??
          decoded.user ??
          decoded.id ??
          savedUser.id ??
          null,
        role: String(
          decoded.user_type ||
            decoded.role ||
            savedUser.user_type ||
            savedUser.role ||
            ""
        )
          .toLowerCase()
          .trim()
          .replace(/_/g, " "),
      };
    } catch {
      return {
        id: savedUser.id ?? null,
        role: String(
          savedUser.user_type || savedUser.role || ""
        )
          .toLowerCase()
          .trim()
          .replace(/_/g, " "),
      };
    }
  }, [token, savedUser]);

  const currentUserId = Number(identity.id);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const filtered = users.filter((user) => {
      const name = getDisplayName(user).toLowerCase();
      const role = String(user.role || "").toLowerCase();

      return !query || name.includes(query) || role.includes(query);
    });

    return [...filtered].sort((a, b) => {
      if (a.is_online !== b.is_online) {
        return a.is_online ? -1 : 1;
      }

      return getDisplayName(a).localeCompare(getDisplayName(b));
    });
  }, [users, searchTerm]);

  const onlineCount = useMemo(
    () => users.filter((user) => user.is_online).length,
    [users]
  );

  const selectedUserName = getDisplayName(selectedUser);

  const selectedUserIsOnline = Boolean(selectedUser?.is_online);

  const handleRedirectToDashboard = useCallback(() => {
    const destinations = {
      admin: "/newAdminDashboard",
      principal: "/newAdminDashboard",
      teacher: "/teacher-dashboard",
      student: "/student-dashboard",
      parent: "/parentsDashboard",
      registry: "/registerDashboard",
      "business manager": "/businessManagerDashboard",
      vpi: "/vpiDashboard",
      vpa: "/vpaDashboard",
      dean: "/deanDashboard",
      "it personnel": "/iTPersonalDashboard",
      "it personel": "/iTPersonalDashboard",
    };

    if (!identity.role) {
      navigate("/login");
      return;
    }

    navigate(destinations[identity.role] || "/");
  }, [identity.role, navigate]);

  const fetchUsers = useCallback(async (showLoader = false) => {
    if (showLoader && mountedRef.current) {
      setUsersLoading(true);
    }

    try {
      const response = await api.get(
        "/api/users/chat/userslist/"
      );

      if (!mountedRef.current) return;

      const nextUsers = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      setUsers(nextUsers);

      // Refresh the selected contact's online status as well.
      setSelectedUser((current) => {
        if (!current) return current;

        return (
          nextUsers.find(
            (user) => Number(user.id) === Number(current.id)
          ) || current
        );
      });
    } catch (error) {
      console.error("Failed to fetch chat users:", error);

      if (mountedRef.current && showLoader) {
        setErrorMessage("Unable to load school members. Please try again.");
      }
    } finally {
      if (mountedRef.current && showLoader) {
        setUsersLoading(false);
      }
    }
  }, []);

  const fetchChatHistory = useCallback(async (userId, options = {}) => {
    if (!userId) return;

    const requestId = ++historyRequestRef.current;

    if (options.showLoader && mountedRef.current) {
      setHistoryLoading(true);
    }

    try {
      const response = await api.get(
        `/api/users/chats/${userId}/chat_history/`
      );

      if (
        !mountedRef.current ||
        requestId !== historyRequestRef.current
      ) {
        return;
      }

      const records = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      setChatHistory((previous) =>
        mergeMessages(previous, records)
      );
    } catch (error) {
      console.error("Error fetching chat history:", error);

      if (mountedRef.current && options.showError) {
        setErrorMessage("Could not load this conversation.");
      }
    } finally {
      if (
        mountedRef.current &&
        requestId === historyRequestRef.current
      ) {
        setHistoryLoading(false);
      }
    }
  }, []);

  const closeSocket = useCallback(() => {
    shouldReconnectRef.current = false;

    clearTimeout(reconnectTimeout.current);

    const currentSocket = socketRef.current;
    socketRef.current = null;

    if (
      currentSocket &&
      (currentSocket.readyState === WebSocket.OPEN ||
        currentSocket.readyState === WebSocket.CONNECTING)
    ) {
      currentSocket.close();
    }

    setConnectionState("disconnected");
  }, []);

  const connectSocket = useCallback((userId) => {
    if (!userId || !token || !mountedRef.current) return;

    shouldReconnectRef.current = true;

    clearTimeout(reconnectTimeout.current);

    const generation = ++socketGenerationRef.current;

    const previousSocket = socketRef.current;
    socketRef.current = null;

    if (
      previousSocket &&
      (previousSocket.readyState === WebSocket.OPEN ||
        previousSocket.readyState === WebSocket.CONNECTING)
    ) {
      previousSocket.close();
    }

    setConnectionState("connecting");

    // Preserve the existing backend WebSocket route.
    const wsUrl =
      `wss://messiahmissioninstitute.online/ws/chat/${userId}/` +
      `?token=${encodeURIComponent(token)}`;

    let ws;

    try {
      ws = new WebSocket(wsUrl);
    } catch (error) {
      console.error("Unable to create chat WebSocket:", error);
      setConnectionState("disconnected");
      return;
    }

    socketRef.current = ws;

    ws.onopen = () => {
      if (
        !mountedRef.current ||
        generation !== socketGenerationRef.current
      ) {
        ws.close();
        return;
      }

      setConnectionState("connected");
      console.info("Chat WebSocket connected.");
    };

    ws.onmessage = (event) => {
      if (
        !mountedRef.current ||
        generation !== socketGenerationRef.current
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);

        if (data.type === "typing") {
          const senderId = getSenderId(
            data.sender_id ?? data.sender
          );

          if (
            senderId === Number(selectedUserRef.current?.id)
          ) {
            setIsTyping(true);

            clearTimeout(typingTimeout.current);

            typingTimeout.current = setTimeout(() => {
              if (mountedRef.current) setIsTyping(false);
            }, 1800);
          }

          return;
        }

        // Ignore non-message WebSocket event types.
        if (
          data.type &&
          !["chat_message", "message", "new_message"].includes(
            data.type
          )
        ) {
          return;
        }

        const incoming = normalizeMessage(data);

        const senderId = getSenderId(incoming.sender);
        const receiverId = getSenderId(incoming.receiver);
        const activeUserId = Number(
          selectedUserRef.current?.id
        );

        if (
          senderId !== activeUserId &&
          receiverId !== activeUserId
        ) {
          return;
        }

        setChatHistory((previous) =>
          mergeMessages(previous, [incoming])
        );
      } catch (error) {
        console.error("Unable to process incoming chat event:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("Chat WebSocket error:", error);
    };

    ws.onclose = () => {
      if (
        generation !== socketGenerationRef.current ||
        !mountedRef.current
      ) {
        return;
      }

      setConnectionState("disconnected");

      if (shouldReconnectRef.current) {
        reconnectTimeout.current = setTimeout(() => {
          if (
            mountedRef.current &&
            shouldReconnectRef.current &&
            Number(selectedUserRef.current?.id) ===
              Number(userId)
          ) {
            connectSocket(userId);
          }
        }, 3000);
      }
    };
  }, [token]);

  useEffect(() => {
    mountedRef.current = true;

    fetchUsers(true);

    const interval = setInterval(() => {
      fetchUsers(false);
    }, 30000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearTimeout(typingTimeout.current);
      clearTimeout(reconnectTimeout.current);

      shouldReconnectRef.current = false;
      socketGenerationRef.current += 1;

      const ws = socketRef.current;
      socketRef.current = null;

      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      ) {
        ws.close();
      }
    };
  }, [fetchUsers]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;

    if (!selectedUser?.id) {
      historyRequestRef.current += 1;
      setChatHistory([]);
      setHistoryLoading(false);
      setIsTyping(false);
      closeSocket();
      return;
    }

    setChatHistory([]);
    setIsTyping(false);

    fetchChatHistory(selectedUser.id, {
      showLoader: true,
      showError: true,
    });

    connectSocket(selectedUser.id);

    return () => {
      shouldReconnectRef.current = false;
      clearTimeout(reconnectTimeout.current);

      const ws = socketRef.current;
      socketRef.current = null;

      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      ) {
        ws.close();
      }
    };
  }, [
    selectedUser?.id,
    fetchChatHistory,
    connectSocket,
    closeSocket,
  ]);

  useEffect(() => {
    if (image) {
      const preview = URL.createObjectURL(image);
      setImagePreview(preview);

      return () => URL.revokeObjectURL(preview);
    }

    setImagePreview(null);
  }, [image]);

  useEffect(() => {
    const container = messagesContainerRef.current;

    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  }, [chatHistory, isTyping, imagePreview]);

  const handleUserClick = (user) => {
    if (Number(user.id) === currentUserId) {
      setNotice("You cannot start a conversation with yourself.");
      return;
    }

    setSelectedUser(user);
    setMobileChatOpen(true);
    setDrawerOpen(false);
  };

  const handleTyping = (value) => {
    setMessage(value);

    if (!selectedUser) return;

    clearTimeout(typingTimeout.current);

    // Typing indicators are sent only when the server supports
    // the existing WebSocket consumer's typing event.
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      try {
        socketRef.current.send(
          JSON.stringify({
            type: "typing",
            receiver: selectedUser.id,
          })
        );
      } catch (error) {
        console.error("Unable to send typing event:", error);
      }
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please select a valid image file.");
      event.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage("Images must be 10 MB or smaller.");
      event.target.value = "";
      return;
    }

    setImage(file);
    event.target.value = "";
  };

  const handleSendMessage = async () => {
    if (
      sendingRef.current ||
      (!message.trim() && !image) ||
      !selectedUser?.id
    ) {
      return;
    }

    if (!token) {
      setErrorMessage("Your session has expired. Please sign in again.");
      navigate("/login");
      return;
    }

    const receiverId = selectedUser.id;
    const messageText = message.trim();
    const selectedImage = image;

    sendingRef.current = true;
    setSending(true);

    try {
      let response;

      if (selectedImage) {
        const formData = new FormData();

        formData.append("receiver", receiverId);
        formData.append("message", messageText);
        formData.append("image", selectedImage);

        response = await api.post(
          "/api/users/chats/",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await api.post(
          "/api/users/chats/",
          {
            receiver: receiverId,
            message: messageText,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      const sentMessage = normalizeMessage(response.data);

      if (mountedRef.current) {
        setMessage("");
        setImage(null);

        setChatHistory((previous) =>
          mergeMessages(previous, [sentMessage])
        );
      }

      // The REST response is the canonical saved message.
      // Do not broadcast the same message a second time here:
      // the existing Django consumer may already broadcast it.
    } catch (error) {
      console.error("Error sending message:", error);

      if (mountedRef.current) {
        setErrorMessage(
          error.response?.data?.detail ||
            "Your message could not be sent. Please try again."
        );
      }
    } finally {
      sendingRef.current = false;

      if (mountedRef.current) {
        setSending(false);
      }
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const renderUserList = (compact = false) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: compact ? "100%" : "100%",
        minHeight: 0,
        bgcolor: COLORS.surface,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          background:
            "linear-gradient(145deg, #075E54 0%, #128C7E 100%)",
          color: "#FFFFFF",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.3}>
            <Box
              sx={{
                width: 43,
                height: 43,
                borderRadius: "15px",
                bgcolor: "rgba(255,255,255,.15)",
                border: "1px solid rgba(255,255,255,.2)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <ForumRoundedIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 19,
                  fontWeight: 850,
                  letterSpacing: "-.5px",
                }}
              >
                Messages
              </Typography>

              <Typography
                sx={{
                  fontSize: 11,
                  opacity: 0.8,
                  letterSpacing: ".4px",
                }}
              >
                SCHOOL COMMUNICATION
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Back to dashboard">
            <IconButton
              onClick={handleRedirectToDashboard}
              aria-label="Back to dashboard"
              sx={{
                color: "#FFFFFF",
                bgcolor: "rgba(255,255,255,.12)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,.22)",
                },
              }}
            >
              <AccountCircleRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box
          sx={{
            mt: 2.5,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderRadius: "15px",
            bgcolor: "rgba(255,255,255,.11)",
            border: "1px solid rgba(255,255,255,.12)",
          }}
        >
          <Box
            sx={{
              width: 39,
              height: 39,
              display: "grid",
              placeItems: "center",
              borderRadius: "12px",
              bgcolor: "rgba(37,211,102,.17)",
              color: "#B9FFD2",
            }}
          >
            <CircleIcon sx={{ fontSize: 12 }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: 12, fontWeight: 750 }}
            >
              Community online
            </Typography>

            <Typography
              sx={{ fontSize: 11, opacity: 0.78, mt: 0.2 }}
            >
              {onlineCount} member{onlineCount !== 1 ? "s" : ""} active
            </Typography>
          </Box>

          <Box
            sx={{
              px: 1.1,
              py: 0.45,
              borderRadius: "8px",
              bgcolor: "rgba(255,255,255,.13)",
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            LIVE
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search people..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          inputProps={{ "aria-label": "Search school members" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  sx={{ color: COLORS.muted, fontSize: 21 }}
                />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <CloseRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "13px",
              bgcolor: "#F5F8FA",
              fontSize: 13,
              "& fieldset": {
                borderColor: "#EDF1F4",
              },
              "&:hover fieldset": {
                borderColor: "#B8D9D0",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.primaryLight,
              },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          px: 2.5,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 850,
            letterSpacing: 1.1,
            color: COLORS.muted,
          }}
        >
          CONTACTS
        </Typography>

        <Chip
          label={filteredUsers.length}
          size="small"
          sx={{
            height: 22,
            fontSize: 10,
            fontWeight: 800,
            bgcolor: "#E8F4F0",
            color: COLORS.primary,
          }}
        />
      </Box>

      <Divider sx={{ borderColor: "#F0F3F5" }} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 1.2,
          py: 1,
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#D9E3E7",
            borderRadius: 10,
          },
        }}
      >
        {usersLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 1.5,
            }}
          >
            <CircularProgress
              size={28}
              sx={{ color: COLORS.primaryLight }}
            />

            <Typography
              sx={{ fontSize: 12, color: COLORS.muted }}
            >
              Loading school members...
            </Typography>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                mx: "auto",
                width: 58,
                height: 58,
                borderRadius: "18px",
                bgcolor: "#F0F5F7",
                display: "grid",
                placeItems: "center",
                color: COLORS.muted,
              }}
            >
              <PersonOutlineRoundedIcon sx={{ fontSize: 29 }} />
            </Box>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13,
                fontWeight: 750,
                color: COLORS.ink,
              }}
            >
              No members found
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 11,
                color: COLORS.muted,
              }}
            >
              Try a different name or search term.
            </Typography>

            <Button
              size="small"
              onClick={() => {
                setSearchTerm("");
                fetchUsers(true);
              }}
              startIcon={<RefreshRoundedIcon />}
              sx={{ mt: 1.5, textTransform: "none" }}
            >
              Refresh contacts
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {filteredUsers.map((user) => {
              const isSelected =
                Number(selectedUser?.id) === Number(user.id);

              return (
                <ListItemButton
                  key={user.id}
                  selected={isSelected}
                  onClick={() => handleUserClick(user)}
                  sx={{
                    px: 1.3,
                    py: 1.4,
                    mb: 0.6,
                    borderRadius: "14px",
                    border: "1px solid transparent",
                    alignItems: "center",
                    transition: "all .18s ease",
                    "&:hover": {
                      bgcolor: "#F2F8F6",
                    },
                    "&.Mui-selected": {
                      bgcolor: "#EAF6F1",
                      borderColor: "#D5EDE3",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "#E4F3EC",
                    },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 53 }}>
                    <ChatAvatar user={user} size={43} />
                  </ListItemAvatar>

                  <ListItemText
                    sx={{ minWidth: 0, my: 0 }}
                    primary={
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 12.5,
                          fontWeight: isSelected ? 850 : 750,
                          color: COLORS.ink,
                        }}
                      >
                        {getDisplayName(user)}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.45 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 10.5,
                            color: user.is_online
                              ? COLORS.primaryLight
                              : COLORS.muted,
                            fontWeight: user.is_online ? 650 : 450,
                          }}
                        >
                          {user.is_online
                            ? "Active now"
                            : formatLastSeen(user)}
                        </Typography>

                        {user.role && (
                          <Typography
                            noWrap
                            sx={{
                              mt: 0.25,
                              fontSize: 10,
                              color: "#98A4AA",
                              textTransform: "capitalize",
                            }}
                          >
                            {String(user.role).replace(/_/g, " ")}
                          </Typography>
                        )}
                      </Box>
                    }
                  />

                  {user.is_online && (
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: COLORS.accent,
                        ml: 0.5,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      <Divider sx={{ borderColor: "#EDF1F3" }} />

      <Box
        sx={{
          p: 2,
          bgcolor: "#FAFCFD",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 32,
              height: 32,
              display: "grid",
              placeItems: "center",
              borderRadius: "10px",
              bgcolor: "#EAF4F0",
              color: COLORS.primary,
            }}
          >
            <AccountCircleRoundedIcon sx={{ fontSize: 19 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 11.5,
                fontWeight: 800,
                color: COLORS.ink,
              }}
            >
              {savedUser.username ||
                savedUser.full_name ||
                "My account"}
            </Typography>

            <Typography
              sx={{ fontSize: 10, color: COLORS.muted }}
            >
              Secure school messaging
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Refresh contacts">
            <IconButton
              size="small"
              onClick={() => fetchUsers(true)}
              aria-label="Refresh contacts"
              sx={{ color: COLORS.primary }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );

  const renderEmptyConversation = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        backgroundImage: `
          linear-gradient(
            rgba(246,250,250,.90),
            rgba(246,250,250,.90)
          ),
          url(${chatWallpaper})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 430,
          textAlign: "center",
          p: { xs: 3, sm: 4 },
          borderRadius: "25px",
          bgcolor: "rgba(255,255,255,.85)",
          border: "1px solid rgba(255,255,255,.9)",
          boxShadow: "0 20px 65px rgba(23,43,50,.07)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Box
          sx={{
            width: 92,
            height: 92,
            mx: "auto",
            display: "grid",
            placeItems: "center",
            position: "relative",
            borderRadius: "28px",
            color: "#FFFFFF",
            background:
              "linear-gradient(145deg, #075E54, #20A58B)",
            boxShadow: "0 15px 35px rgba(7,94,84,.22)",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: -8,
              borderRadius: "33px",
              border: "1px solid rgba(18,140,126,.17)",
            },
          }}
        >
          <ChatBubbleOutlineRoundedIcon
            sx={{ fontSize: 44 }}
          />
        </Box>

        <Typography
          sx={{
            mt: 3,
            fontSize: { xs: 23, sm: 27 },
            fontWeight: 900,
            color: COLORS.ink,
            letterSpacing: "-1px",
          }}
        >
          Your conversations,
          <br />
          all in one place.
        </Typography>

        <Typography
          sx={{
            maxWidth: 310,
            mx: "auto",
            mt: 1.5,
            fontSize: 13,
            lineHeight: 1.9,
            color: COLORS.muted,
          }}
        >
          Connect with teachers, students, parents, and
          school staff through your communication portal.
        </Typography>

        <Button
          variant="contained"
          onClick={() => setDrawerOpen(true)}
          startIcon={<ForumRoundedIcon />}
          sx={{
            mt: 3,
            px: 3,
            py: 1.25,
            borderRadius: "12px",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 800,
            bgcolor: COLORS.primary,
            boxShadow: "0 8px 18px rgba(7,94,84,.17)",
            "&:hover": { bgcolor: COLORS.primaryLight },
          }}
        >
          Find a conversation
        </Button>

        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={0.8}
          sx={{ mt: 3 }}
        >
          <CircleIcon
            sx={{ fontSize: 8, color: COLORS.accent }}
          />

          <Typography
            sx={{
              fontSize: 10,
              color: COLORS.muted,
              letterSpacing: ".3px",
            }}
          >
            {onlineCount} MEMBERS CURRENTLY ONLINE
          </Typography>
        </Stack>
      </Box>
    </Box>
  );

  const renderMessages = () => (
    <Box
      ref={messagesContainerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: { xs: 1.5, sm: 3, md: 4, lg: 6 },
        py: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.4,
        backgroundImage: `
          linear-gradient(
            rgba(242,247,246,.91),
            rgba(242,247,246,.91)
          ),
          url(${chatWallpaper})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "local",
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: "#D5E1E2",
          borderRadius: 10,
        },
      }}
    >
      {historyLoading && chatHistory.length === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            gap: 1.5,
          }}
        >
          <CircularProgress
            size={24}
            sx={{ color: COLORS.primaryLight }}
          />

          <Typography
            sx={{ fontSize: 12, color: COLORS.muted }}
          >
            Loading conversation...
          </Typography>
        </Box>
      )}

      {!historyLoading && chatHistory.length === 0 && (
        <Box
          sx={{
            my: "auto",
            alignSelf: "center",
            maxWidth: 330,
            p: 2.5,
            textAlign: "center",
            borderRadius: "20px",
            bgcolor: "rgba(255,255,255,.84)",
            border: "1px solid rgba(255,255,255,.95)",
            boxShadow: "0 8px 30px rgba(25,55,60,.04)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              mx: "auto",
              borderRadius: "16px",
              bgcolor: "#E9F5F0",
              color: COLORS.primary,
              display: "grid",
              placeItems: "center",
            }}
          >
            <ChatBubbleOutlineRoundedIcon />
          </Box>

          <Typography
            sx={{
              mt: 1.5,
              fontSize: 14,
              fontWeight: 850,
              color: COLORS.ink,
            }}
          >
            Start the conversation
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: 11.5,
              lineHeight: 1.8,
              color: COLORS.muted,
            }}
          >
            Send a message to {selectedUserName} to get
            the conversation started.
          </Typography>
        </Box>
      )}

      {chatHistory.map((chat, index) => {
        const senderId = getSenderId(chat.sender);
        const isSender = senderId === currentUserId;

        const senderUser =
          users.find(
            (user) => Number(user.id) === senderId
          ) || (isSender ? savedUser : selectedUser);

        const avatarSrc =
          chat.sender_profile_image ||
          senderUser?.profile_image ||
          undefined;

        const previousMessage = chatHistory[index - 1];

        const previousSenderId = previousMessage
          ? getSenderId(previousMessage.sender)
          : null;

        const showAvatar =
          !isSender && previousSenderId !== senderId;

        return (
          <Box
            key={chat.id ?? `${chat.timestamp}-${index}`}
            sx={{
              display: "flex",
              justifyContent: isSender
                ? "flex-end"
                : "flex-start",
              alignItems: "flex-end",
              gap: 1,
              width: "100%",
            }}
          >
            {!isSender && (
              <Box
                sx={{
                  width: 30,
                  flexShrink: 0,
                  mb: 0.3,
                }}
              >
                {showAvatar && (
                  <Avatar
                    src={avatarSrc}
                    alt={getDisplayName(senderUser)}
                    sx={{
                      width: 29,
                      height: 29,
                      fontSize: 11,
                      bgcolor: "#E0F2EC",
                      color: COLORS.primary,
                    }}
                  >
                    {getInitials(getDisplayName(senderUser))}
                  </Avatar>
                )}
              </Box>
            )}

            <Paper
              elevation={0}
              sx={{
                maxWidth: {
                  xs: "88%",
                  sm: "78%",
                  md: "67%",
                  lg: "60%",
                },
                minWidth: 65,
                px: 1.8,
                pt: 1.35,
                pb: 0.85,
                borderRadius: isSender
                  ? "17px 17px 5px 17px"
                  : "17px 17px 17px 5px",
                bgcolor: isSender
                  ? COLORS.outgoing
                  : COLORS.incoming,
                border: isSender
                  ? "1px solid rgba(116,185,105,.14)"
                  : "1px solid rgba(225,232,233,.9)",
                boxShadow: "0 3px 9px rgba(30,54,58,.045)",
                overflow: "hidden",
                overflowWrap: "anywhere",
              }}
            >
              {chat.message && (
                <Typography
                  sx={{
                    fontSize: 13.5,
                    lineHeight: 1.75,
                    color: "#25353A",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {chat.message}
                </Typography>
              )}

              {chat.image && (
                <Box
                  component="img"
                  src={chat.image}
                  alt="Message attachment"
                  loading="lazy"
                  onClick={() => {
                    window.open(
                      chat.image,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                  sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: 340,
                    maxHeight: 330,
                    objectFit: "cover",
                    borderRadius: "12px",
                    mt: chat.message ? 1 : 0,
                    cursor: "pointer",
                    border: "1px solid rgba(20,40,45,.07)",
                    transition: "opacity .2s ease",
                    "&:hover": { opacity: 0.92 },
                  }}
                />
              )}

              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={0.5}
                sx={{ mt: 0.7 }}
              >
                <Typography
                  sx={{
                    fontSize: 9.5,
                    color: "#7D8A89",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatTime(chat.timestamp)}
                </Typography>

                {isSender && (
                  <DoneAllRoundedIcon
                    sx={{
                      fontSize: 14,
                      color: "#559B8B",
                    }}
                  />
                )}
              </Stack>
            </Paper>
          </Box>
        );
      })}

      {isTyping && (
        <Box
          sx={{
            alignSelf: "flex-start",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,.92)",
            border: "1px solid #E9EFF0",
          }}
        >
          <Stack direction="row" spacing={0.4}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: COLORS.primaryLight,
                  opacity: 0.65,
                }}
              />
            ))}
          </Stack>

          <Typography
            sx={{ fontSize: 10.5, color: COLORS.muted }}
          >
            {selectedUserName} is typing...
          </Typography>
        </Box>
      )}

      <div ref={chatEndRef} />
    </Box>
  );

  const renderComposer = () => (
    <Box
      sx={{
        flexShrink: 0,
        p: { xs: 1.2, sm: 2 },
        pt: { xs: 1, sm: 1.5 },
        bgcolor: "#FFFFFF",
        borderTop: "1px solid #E9EFF1",
        boxShadow: "0 -6px 20px rgba(28,48,55,.025)",
      }}
    >
      {imagePreview && (
        <Fade in>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              p: 1,
              border: "1px solid #E5ECEE",
              borderRadius: "14px",
              bgcolor: "#F7FAFA",
            }}
          >
            <Box
              component="img"
              src={imagePreview}
              alt="Selected attachment preview"
              sx={{
                width: 56,
                height: 56,
                objectFit: "cover",
                borderRadius: "10px",
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 12,
                  fontWeight: 750,
                  color: COLORS.ink,
                }}
              >
                {image?.name}
              </Typography>

              <Typography
                sx={{ fontSize: 10, color: COLORS.muted, mt: 0.4 }}
              >
                {image
                  ? `${(image.size / 1024).toFixed(0)} KB`
                  : "Image attachment"}
              </Typography>
            </Box>

            <Tooltip title="Remove attachment">
              <IconButton
                size="small"
                onClick={() => setImage(null)}
                aria-label="Remove image attachment"
                sx={{
                  color: COLORS.danger,
                  bgcolor: "#FFF0F0",
                  "&:hover": { bgcolor: "#FFE4E4" },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Fade>
      )}

      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={1}
      >
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          hidden
        />

        <Tooltip title="Attach an image">
          <span>
            <IconButton
              onClick={() => imageInputRef.current?.click()}
              disabled={sending}
              aria-label="Attach image"
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: "14px",
                color: COLORS.primary,
                bgcolor: "#EDF6F2",
                border: "1px solid #E1EFE9",
                "&:hover": {
                  bgcolor: "#DFF0E8",
                },
              }}
            >
              <ImageRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          fullWidth
          multiline
          maxRows={5}
          placeholder={`Message ${selectedUserName}...`}
          value={message}
          onChange={(event) => handleTyping(event.target.value)}
          onKeyDown={handleComposerKeyDown}
          disabled={sending}
          inputProps={{
            "aria-label": "Write a message",
            maxLength: 10000,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 46,
              alignItems: "center",
              borderRadius: "15px",
              bgcolor: "#F5F8F9",
              py: 0.7,
              px: 1.5,
              fontSize: 13,
              "& fieldset": {
                borderColor: "#E9EFF1",
              },
              "&:hover fieldset": {
                borderColor: "#B9D9D0",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.primaryLight,
                borderWidth: 1,
              },
            },
            "& textarea": {
              lineHeight: 1.65,
            },
          }}
        />

        <Tooltip title="Send message">
          <span>
            <IconButton
              onClick={handleSendMessage}
              disabled={
                sending || (!message.trim() && !image)
              }
              aria-label="Send message"
              sx={{
                width: 46,
                height: 46,
                flexShrink: 0,
                borderRadius: "15px",
                color: "#FFFFFF",
                background:
                  "linear-gradient(140deg, #128C7E, #075E54)",
                boxShadow: "0 6px 15px rgba(7,94,84,.2)",
                transition: "all .2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(140deg, #159F8E, #075E54)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  bgcolor: "#E5EAEC",
                  background: "#E5EAEC",
                  color: "#A5B0B4",
                  boxShadow: "none",
                },
              }}
            >
              {sending ? (
                <CircularProgress
                  size={21}
                  sx={{ color: "inherit" }}
                />
              ) : (
                <SendRoundedIcon sx={{ fontSize: 21 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1, px: 0.5 }}
      >
        <Typography
          sx={{ fontSize: 9.5, color: "#9AA6AB" }}
        >
          Enter to send · Shift + Enter for a new line
        </Typography>

        <Typography
          sx={{ fontSize: 9.5, color: "#9AA6AB" }}
        >
          {connectionState === "connected"
            ? "Live connection"
            : connectionState === "connecting"
              ? "Connecting..."
              : "Reconnecting..."}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100dvh",
        minHeight: 0,
        display: "flex",
        overflow: "hidden",
        bgcolor: COLORS.canvas,
        fontFamily:
          '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        color: COLORS.ink,
      }}
    >
      {/* DESKTOP CONTACT SIDEBAR */}
      <Paper
        square
        elevation={0}
        sx={{
          width: { md: 310, lg: 350 },
          flexShrink: 0,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          borderRight: "1px solid #E3E9EC",
          bgcolor: "#FFFFFF",
        }}
      >
        {renderUserList()}
      </Paper>

      {/* MOBILE CONTACT DRAWER */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: "min(340px, 88vw)", sm: 350 },
            maxWidth: "100%",
            overflow: "hidden",
          },
        }}
      >
        {renderUserList(true)}
      </Drawer>

      {/* MAIN CHAT WORKSPACE */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          bgcolor: "#F5F8F8",
        }}
      >
        {/* TOP HEADER */}
        <Box
          sx={{
            flexShrink: 0,
            minHeight: 76,
            px: { xs: 1.5, sm: 2.5, md: 3 },
            py: 1.3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            bgcolor: "#FFFFFF",
            borderBottom: "1px solid #E7ECEE",
            boxShadow: "0 3px 15px rgba(30,50,55,.025)",
            zIndex: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 1, sm: 1.5 }}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <IconButton
              onClick={() => {
                if (mobileChatOpen && selectedUser) {
                  setMobileChatOpen(false);
                  setSelectedUser(null);
                } else {
                  setDrawerOpen(true);
                }
              }}
              aria-label={
                mobileChatOpen
                  ? "Back to contacts"
                  : "Open contacts"
              }
              sx={{
                display: { xs: "inline-flex", md: "none" },
                color: COLORS.primary,
                bgcolor: "#EDF6F2",
                borderRadius: "12px",
                flexShrink: 0,
              }}
            >
              {mobileChatOpen ? (
                <ArrowBackIosNewRoundedIcon
                  sx={{ fontSize: 19 }}
                />
              ) : (
                <MenuRoundedIcon />
              )}
            </IconButton>

            {selectedUser ? (
              <>
                <ChatAvatar
                  user={selectedUser}
                  size={44}
                />

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: { xs: 13, sm: 15 },
                      fontWeight: 850,
                      color: COLORS.ink,
                      letterSpacing: "-.3px",
                    }}
                  >
                    {selectedUserName}
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.65}
                    sx={{ mt: 0.4 }}
                  >
                    <CircleIcon
                      sx={{
                        fontSize: 8,
                        color: selectedUserIsOnline
                          ? COLORS.accent
                          : "#A7B0B4",
                      }}
                    />

                    <Typography
                      noWrap
                      sx={{
                        fontSize: 10.5,
                        color: selectedUserIsOnline
                          ? COLORS.primaryLight
                          : COLORS.muted,
                      }}
                    >
                      {formatLastSeen(selectedUser)}
                      {selectedUser?.role
                        ? ` · ${String(
                            selectedUser.role
                          ).replace(/_/g, " ")}`
                        : ""}
                    </Typography>
                  </Stack>
                </Box>
              </>
            ) : (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 15, sm: 18 },
                    fontWeight: 900,
                    color: COLORS.ink,
                    letterSpacing: "-.6px",
                  }}
                >
                  School Communication
                </Typography>

                <Typography
                  sx={{
                    fontSize: 10.5,
                    mt: 0.3,
                    color: COLORS.muted,
                  }}
                >
                  Your central messaging workspace
                </Typography>
              </Box>
            )}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.8}
          >
            {selectedUser && (
              <Chip
                icon={
                  <CircleIcon
                    sx={{
                      fontSize: "9px !important",
                      color: `${
                        connectionState === "connected"
                          ? COLORS.accent
                          : "#9BA9AE"
                      } !important`,
                    }}
                  />
                }
                // label={
                //   connectionState === "connected"
                //     ? "Connected"
                //     : connectionState === "connecting"
                //       ? "Connecting"
                //       : "Offline"
                // }
                size="small"
                sx={{
                  display: { xs: "none", sm: "flex" },
                  bgcolor:
                    connectionState === "connected"
                      ? "#ECF8F1"
                      : "#F1F4F5",
                  color:
                    connectionState === "connected"
                      ? COLORS.primary
                      : COLORS.muted,
                  fontSize: 10,
                  fontWeight: 750,
                  borderRadius: "9px",
                }}
              />
            )}

            {selectedUser && (
              <Tooltip title="Refresh conversation">
                <IconButton
                  onClick={() =>
                    fetchChatHistory(selectedUser.id, {
                      showLoader: true,
                      showError: true,
                    })
                  }
                  aria-label="Refresh conversation"
                  sx={{
                    color: COLORS.muted,
                    bgcolor: "#F5F8F9",
                    borderRadius: "12px",
                    "&:hover": {
                      color: COLORS.primary,
                      bgcolor: "#EDF6F2",
                    },
                  }}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Back to dashboard">
              <IconButton
                onClick={handleRedirectToDashboard}
                aria-label="Back to dashboard"
                sx={{
                  color: COLORS.primary,
                  bgcolor: "#EDF6F2",
                  borderRadius: "12px",
                  "&:hover": { bgcolor: "#DFEFE8" },
                }}
              >
                <AccountCircleRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* CHAT OR WELCOME SCREEN */}
        {selectedUser ? (
          <>
            {renderMessages()}
            {renderComposer()}
          </>
        ) : (
          renderEmptyConversation()
        )}

        {/* GLOBAL NOTIFICATIONS */}
        <Snackbar
          open={Boolean(errorMessage)}
          autoHideDuration={5000}
          onClose={() => setErrorMessage("")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setErrorMessage("")}
            sx={{
              borderRadius: "12px",
              boxShadow: "0 8px 25px rgba(0,0,0,.12)",
            }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={Boolean(notice)}
          autoHideDuration={3000}
          onClose={() => setNotice("")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            severity="info"
            onClose={() => setNotice("")}
            sx={{ borderRadius: "12px" }}
          >
            {notice}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ChatApp;
